import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

export default function OTPInput({ length = 6, onChangeOTP, otp, setOtp }) {
  const inputsRef = useRef([]);

  // Initialize OTP state with empty strings
  useEffect(() => {
    if (!otp || otp.length !== length) {
      const initialOtp = Array(length).fill("");
      setOtp(initialOtp);
    }
  }, [length, setOtp, otp]);

  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    onChangeOTP && onChangeOTP(newOtp.join(""));

    // Move focus to next input
    if (index < length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      // If current input is empty and backspace is pressed, move to previous input
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        onChangeOTP && onChangeOTP(newOtp.join(""));
        inputsRef.current[index - 1].focus();
      } else if (otp[index]) {
        // Clear current input if it has value
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
        onChangeOTP && onChangeOTP(newOtp.join(""));
      }
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").replace(/[^0-9]/g, "");

    if (!pastedData) return;

    const newOtp = [...otp];
    const pastedDigits = pastedData.split("").slice(0, length);

    // Fill OTP array starting from current index
    for (let i = 0; i < pastedDigits.length; i++) {
      const targetIndex = index + i;
      if (targetIndex < length) {
        newOtp[targetIndex] = pastedDigits[i];
      }
    }

    setOtp(newOtp);
    onChangeOTP && onChangeOTP(newOtp.join(""));

    // Move focus to appropriate field
    const nextIndex = Math.min(index + pastedDigits.length, length - 1);
    if (inputsRef.current[nextIndex]) {
      inputsRef.current[nextIndex].focus();
    } else if (inputsRef.current[length - 1]) {
      inputsRef.current[length - 1].focus();
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
      {Array.from({ length }, (_, i) => (
        <TextField
          key={i}
          inputRef={(el) => (inputsRef.current[i] = el)}
          value={otp[i] || ""}
          onChange={(e) => handleChange(e.target, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          onFocus={(e) => e.target.select()} // Select all text when focused
          variant="outlined"
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: "1.5rem",
              width: "3rem",
              height: "3rem",
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#ccc",
              },
              "&:hover fieldset": {
                borderColor: "#680000",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#680000",
                borderWidth: 2,
              },
            },
          }}
        />
      ))}
    </Box>
  );
}